import React, { useContext, useEffect } from 'react';
import {
  Alert,
  Box,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ReactPhoneInput from 'react-phone-input-material-ui';

import { counselorDetailsSchema } from 'utils/schema';
import { NO, permissions, YES } from 'utils/dropDownList';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getProviderDetails, updateProviderDetails } from 'store/slices/userSlice';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { toast } from 'react-toastify';
import { AuthContext, useAuth } from 'utils/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import { Restricted } from 'components/Restricted';
import Autocomplete from '@mui/material/Autocomplete';
import { useState, useMemo } from 'react';
import { getOfficeSearchListByStatusAndType } from 'store/slices/secureMessageSlice';
import { IOfficeSearchDetails } from 'store/interfaces/IUserDetails';

const CounselorInfo = (props: any) => {
  const isProfileDetailsFetched = useAppSelector(
    (state) => state.user.loaders.isProfileDetailsFetched
  );
  const { user } = useContext(AuthContext);
  const { state } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchOffices, setSearchOffices] = useState<Array<IOfficeSearchDetails>>([]);
  const [newCouselor, setNewCouselor] = useState<any>(null);
  const [newOfficeId, setNewOfficeId] = useState<any>(null);
  const [officeStatus, setOfficeStatus] = useState<any>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    setValue,
    reset,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(counselorDetailsSchema),
    mode: 'all',
  });

  const searchInput = (value: any) => {
    setValue('officeName', value, { shouldValidate: true, shouldDirty: true });
    if (searchOffices?.length === 0) {
      return;
    }
    if (value && value.length >= 3) {
      const matches = searchOffices.filter((office) =>
        office.officeName.toLowerCase().includes(value.toLowerCase())
      );
      if (matches?.length === 1 && matches[0]?.officeName.toLowerCase() === value.toLowerCase()) {
        setOfficeName(matches[0]);
      } else {
        setNewOfficeId(null);
        setOfficeStatus(null);
      }
    } else {
      setNewOfficeId(null);
      setOfficeStatus(null);
    }
  };

  const setOfficeName = (value: any) => {
    if (typeof value === 'object' && value !== null) {
      setNewOfficeId(value.id);
      setOfficeStatus(value.status);
      setValue('officeName', value.officeName, { shouldValidate: true, shouldDirty: true });
      setValue('officePhoneNumber', value.officePhoneNumber, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('officeFaxNumber', value.officeFaxNumber, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleNewCouselor = (e: any) => {
    if (e.target.value === YES) {
      setNewCouselor(YES);
    } else {
      setNewCouselor(NO);
    }
    setValue('counselorOrTherapist', e.target.value, { shouldValidate: true });
  };

  const getOfficeSearch = () => {
    if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
      const data = { status: 'Approved', type: 'Counselor' };
      dispatch(getOfficeSearchListByStatusAndType(data))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            setSearchOffices(response.data);
          }
        });
    }
  };

  const getProvider = () => {
    if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
      const data = {
        patientId: props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId,
        type: 'Counselor',
      };
      dispatch(getProviderDetails(data))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            setNewCouselor(response?.data?.counselorOrTherapist);
            Object.keys(response.data).forEach((key: any) => {
              if (response.data[key] !== null) {
                setValue(key, response.data[key], { shouldValidate: true, shouldDirty: false });
                if (key === 'officeStatus') {
                  setOfficeStatus(response.data[key]);
                }
              }
            });
          }
        });
    } else {
      const message = 'Please add patient details first.';
      toast(message, {
        type: 'info',
        icon: true,
      });
      navigate(ROUTE.PATIENT_INFORMATION);
    }
  };

  useEffect(() => {
    getOfficeSearch();
    getProvider();
    //eslint-disable-next-line
  }, []);

  const getPayload = (formData: any) => {
    const { officeName, firstName, lastName, officePhoneNumber, officeFaxNumber } = formData;
    return {
      newOfficeId: newOfficeId,
      officeName,
      firstName,
      lastName,
      officePhoneNumber,
      officeFaxNumber,
      counselorOrTherapist: newCouselor,
    };
  };

  const onSubmitForm = (formData: any) => {
    if (isValid && isDirty) {
      if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
        const payload = getPayload(formData);
        dispatch(
          updateProviderDetails({
            patientId:
              props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId,
            payload,
            type: 'Counselor',
          })
        )
          .unwrap()
          .then((response: any) => {
            if (response.status === 200) {
              const message = 'Provider details updated successfully.';
              toast(message, {
                type: 'success',
                icon: true,
              });
              reset(formData);
              props?.onComplete();
            }
          });
      }
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  return (
    <Container component='main' maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component='form'
          noValidate
          sx={{ mt: 1 }}
          onSubmit={handleSubmit((formData) => onSubmitForm(formData))}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity='info' sx={{ fontSize: '13px', lineHeight: '20px' }}>
                Start typing and select the correct counselor from the list. If no match appears,
                add a new provider.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <InputLabel
                htmlFor='custody'
                sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}
              >
                <b>Does the child currently see a counselor or mental health therapist? </b>
              </InputLabel>
              <FormControl>
                <RadioGroup
                  row
                  onChange={handleNewCouselor}
                  name='counselorOrTherapist'
                  value={watch('counselorOrTherapist')}
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                    {...register('counselorOrTherapist')}
                    checked={newCouselor === 'yes'}
                  />
                  <FormControlLabel
                    value='no'
                    control={<Radio />}
                    label='No'
                    {...register('counselorOrTherapist')}
                    checked={newCouselor === 'no' || newCouselor === null}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {newCouselor === 'yes' ? (
              <>
                <Grid item xs={12}>
                  <InputLabel htmlFor='office-name'>Practice or Organization Name*</InputLabel>
                  <Autocomplete
                    freeSolo
                    options={searchOffices}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.officeName
                    }
                    onInputChange={(_, value) => searchInput(value)}
                    onChange={(_, value) => {
                      setOfficeName(value);
                    }}
                    disabled={newCouselor !== 'yes'}
                    value={watch('officeName') ? watch('officeName') : null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        placeholder="Enter counselor's office name"
                        id='office-name'
                        autoComplete='officeName'
                        error={!!errors.officeName}
                        helperText={errors?.officeName?.message}
                        disabled={
                          newCouselor !== 'yes' || !user?.permissions.includes('EDIT_PROFILE')
                        }
                        inputProps={{
                          ...params.inputProps,
                          maxLength: 255,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor='last-name'>Counselor Last Name*</InputLabel>
                  <TextField
                    required
                    fullWidth
                    placeholder="Enter counselor's last name"
                    id='lastName'
                    autoComplete='lastName'
                    {...register('lastName')}
                    error={!!errors.lastName}
                    helperText={errors?.lastName?.message}
                    disabled={newCouselor !== 'yes' || !user?.permissions.includes('EDIT_PROFILE')}
                    inputProps={{
                      maxLength: 255,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor='firstName'>Counselor First Name*</InputLabel>
                  <TextField
                    required
                    fullWidth
                    placeholder="Enter counselor's first name"
                    id='first-name'
                    autoComplete='firstName'
                    {...register('firstName')}
                    error={!!errors.firstName}
                    helperText={errors?.firstName?.message?.toString()}
                    disabled={newCouselor !== 'yes' || !user?.permissions.includes('EDIT_PROFILE')}
                    inputProps={{
                      maxLength: 255,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor='phone'>
                    {props?.from !== 'admin' ? 'Office Phone Number*' : 'Office Phone Number'}
                  </InputLabel>
                  <ReactPhoneInput
                    component={TextField}
                    label=''
                    placeholder='+1 (555) 000-0000'
                    onlyCountries={['us']}
                    country={'us'}
                    masks={{ us: '(...) ...-....' }}
                    autoFormat={true}
                    disableDropdown={true}
                    defaultErrorMessage={'Please enter a phone number'}
                    {...register('officePhoneNumber')}
                    inputProps={{
                      helperText: errors.officePhoneNumber ? errors.officePhoneNumber?.message : '',
                      error: !!errors.officePhoneNumber,
                    }}
                    countryCodeEditable={false}
                    onChange={(e) => {
                      setValue('officePhoneNumber', e, { shouldValidate: true, shouldDirty: true });
                    }}
                    value={watch('officePhoneNumber') ? watch('officePhoneNumber') : null}
                    disabled={
                      newCouselor !== 'yes' ||
                      officeStatus === 'Approved' ||
                      !user?.permissions.includes('EDIT_PROFILE')
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor='faxNumber'>Office&apos;s Fax Number</InputLabel>
                  <ReactPhoneInput
                    component={TextField}
                    label=''
                    placeholder='+1 (555) 000-0000'
                    onlyCountries={['us']}
                    country={'us'}
                    masks={{ us: '(...) ...-....' }}
                    autoFormat={true}
                    disableDropdown={true}
                    defaultErrorMessage={'Please enter a fax number'}
                    {...register('officeFaxNumber')}
                    inputProps={{
                      helperText: errors?.officeFaxNumber ? errors.officeFaxNumber?.message : '',
                      error: !!errors.officeFaxNumber,
                    }}
                    countryCodeEditable={false}
                    onChange={(e) => {
                      setValue('officeFaxNumber', e, { shouldValidate: true, shouldDirty: true });
                    }}
                    value={watch('officeFaxNumber') ? watch('officeFaxNumber') : ''}
                    onBlur={() => {
                      if (watch('officeFaxNumber') === '1') clearErrors('officeFaxNumber');
                    }}
                    disabled={
                      newCouselor !== 'yes' ||
                      officeStatus === 'Approved' ||
                      !user?.permissions.includes('EDIT_PROFILE')
                    }
                  />
                </Grid>
              </>
            ) : (
              <></>
            )}
            <Restricted permission={permissions.EDIT_PROFILE}>
              <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                <LoadingButton
                  loading={isProfileDetailsFetched}
                  variant='contained'
                  // loadingPosition='start'
                  type='submit'
                  sx={{
                    minWidth: 140,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  Save
                </LoadingButton>
              </Grid>
            </Restricted>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default CounselorInfo;
